<script setup>
/**
 * 快乐十分（klsf）界面还原预览页 —— 对应旧版 chunk web/26.9890323d37f84b24c368.js。
 *
 * 旧版该页的玩法为「号码 / 总和 / 总和大小」，开奖 8 个号码取自 1-20
 * （旧版样式类 lottery-ball-19 / -20 / -21 用于号码球着色）。
 * 后端 games/ 目录下没有 klsf 游戏，因此本页不接任何接口，全部为占位数据。
 */
import { computed, ref } from 'vue'
import GamePreviewShell from '../components/GamePreviewShell.vue'

// 与赛车台同一组号码配色，1-20 循环取用，保证与其它盘面观感一致。
const palette = ['olive', 'blue', 'black', 'orange', 'teal', 'purple', 'gray', 'red', 'brown', 'green']
const types = [
  { key: 'number', title: '号码' },
  { key: 'sum', title: '总和' },
  { key: 'sumdx', title: '总和大小' },
]
// 占位赔率，接后端后应改为读取 /home/game/odds。
const sumRange = Array.from({ length: 45 }, (_, index) => index + 36)
const propPlays = [['总和大', '1.950'], ['总和小', '1.950'], ['总和单', '1.950'], ['总和双', '1.950']]
const placeholderLottery = { period: '20260806041', balls: [3, 17, 8, 20, 11, 1, 14, 6] }
const placeholderHistory = [
  { no: '20260806040', balls: [12, 5, 19, 2, 16, 9, 20, 7] },
  { no: '20260806039', balls: [4, 18, 10, 13, 1, 15, 6, 11] },
  { no: '20260806038', balls: [20, 8, 3, 17, 14, 2, 9, 5] },
]

const activeType = ref('number')
const selected = ref(new Set())

function color(number) { return palette[(Number(number) - 1) % palette.length] }
const numbers = computed(() => Array.from({ length: 20 }, (_, index) => index + 1))
const lotterySum = computed(() => placeholderLottery.balls.reduce((sum, ball) => sum + ball, 0))
// 快乐十分取 8 个号码，理论和值范围 36-164，中位数 100，>=101 为大。
const summary = computed(() => `总和 ${lotterySum.value} ${lotterySum.value >= 101 ? '大' : '小'}${lotterySum.value % 2 ? '单' : '双'}`)

function toggle(key) {
  const next = new Set(selected.value)
  next.has(key) ? next.delete(key) : next.add(key)
  selected.value = next
}
function reset() { selected.value = new Set() }
</script>

<template>
  <GamePreviewShell
    title="快乐十分"
    :period="placeholderLottery.period"
    :countdown="112"
    :total-bet="0"
    :selected-count="selected.size"
    @reset="reset"
  >
    <template #lottery-summary><span class="summary">{{ summary }}</span></template>

    <template #lottery>
      <i v-for="(ball, index) in placeholderLottery.balls" :key="`${ball}-${index}`" :class="['lottery-ball', color(ball)]">{{ String(ball).padStart(2, '0') }}</i>
    </template>

    <template #history>
      <article v-for="row in placeholderHistory" :key="row.no" class="history-row">
        <span>{{ row.no }}</span>
        <div><i v-for="(ball, index) in row.balls" :key="`${row.no}-${index}`" :class="color(ball)">{{ String(ball).padStart(2, '0') }}</i></div>
        <b>{{ row.balls.reduce((sum, ball) => sum + ball, 0) }}</b>
      </article>
    </template>

    <template #types>
      <button
        v-for="type in types"
        :key="type.key"
        type="button"
        :class="['bet-type-item', { 'bet-type-active': activeType === type.key }]"
        @click="activeType = type.key"
      >{{ type.title }}</button>
    </template>

    <template #board>
      <div v-if="activeType === 'number'" class="board-grid grid-5">
        <button
          v-for="number in numbers"
          :key="number"
          type="button"
          :class="['board-item', { selected: selected.has(`num-${number}`) }]"
          @click="toggle(`num-${number}`)"
        >
          <i :class="['board-ball', color(number)]">{{ String(number).padStart(2, '0') }}</i>
          <small class="odds">2.400</small>
        </button>
      </div>

      <div v-else-if="activeType === 'sum'" class="board-grid grid-6">
        <button
          v-for="value in sumRange"
          :key="value"
          type="button"
          :class="['board-item', 'text-item', { selected: selected.has(`sum-${value}`) }]"
          @click="toggle(`sum-${value}`)"
        >
          <strong>{{ value }}</strong>
          <small class="odds">—</small>
        </button>
      </div>

      <div v-else class="board-grid grid-4">
        <button
          v-for="[label, odds] in propPlays"
          :key="label"
          type="button"
          :class="['board-item', 'text-item', { selected: selected.has(`prop-${label}`) }]"
          @click="toggle(`prop-${label}`)"
        >
          <strong>{{ label }}</strong>
          <small class="odds">{{ odds }}</small>
        </button>
      </div>
    </template>
  </GamePreviewShell>
</template>

<style scoped>
.summary{color:#111;font-size:12px}
.lottery-ball{display:inline-grid;width:28px;height:29px;place-items:center;border-radius:5px;color:#fff;font-size:14px;font-style:normal}
.olive{background:#d8dc00}.blue{background:#008ed4}.black{background:#343434}.orange{background:#f06a00}.teal{background:#0ccbd1}.purple{background:#4c2ee8}.gray{background:#b5b7b7}.red{background:#ef2b16}.brown{background:#850a03}.green{background:#00a90d}
.history-row{display:grid;grid-template-columns:100px 1fr 34px;min-height:32px;align-items:center;padding:0 11px;border-bottom:1px solid #e5e5e5}
.history-row>div{display:flex;gap:4px}
.history-row i{display:inline-grid;width:22px;height:22px;place-items:center;border-radius:4px;color:#fff;font-size:11px;font-style:normal}
.history-row b{color:#e6373d;text-align:right;font-weight:600}
.bet-type-item{flex:0 0 auto;border:0;border-right:1px solid #eee;background:#fff;color:#444;padding:9px 16px;font-size:13px;white-space:nowrap}
.bet-type-active{background:#f04141;color:#fff}
.board-grid{display:grid;gap:8px}
.grid-4{grid-template-columns:repeat(4,1fr)}
.grid-5{grid-template-columns:repeat(5,1fr)}
.grid-6{grid-template-columns:repeat(6,1fr)}
.board-item{display:grid;place-items:center;gap:3px;border:1px solid #77c697;background:#389b61;color:#fff;padding:6px 2px}
.board-item.selected{border-color:#ffe372;background:#24804e;box-shadow:inset 0 0 0 1px #ffe372}
.board-ball{display:inline-grid;width:26px;height:26px;place-items:center;border-radius:5px;color:#fff;font-size:12px;font-style:normal}
.text-item{min-height:44px}
.text-item strong{font-size:15px;font-weight:600}
.odds{color:#ffe561;font-size:10px}
</style>
